/**
 * Timing Engine - High-precision timestamp management
 * Core of ManaTiming's functionality
 */

import { saveLocal, loadLocal, queueForSync, STORAGE_KEYS, getSelectedRace, getRaceKey } from './storage.js'
import { generateUUID } from './uuid.js'

// Race state
let raceStartTime = null
let timestamps = []
let deviceId = null
let raceFinished = false
let masterTimerLock = null // { raceId, masterDeviceId, claimedAt }

/**
 * Initialize the timing engine
 */
export function initTiming() {
  // Generate or retrieve device ID for multi-device coordination
  deviceId = loadLocal('manatiming_device_id')
  if (!deviceId) {
    deviceId = generateUUID()
    saveLocal('manatiming_device_id', deviceId)
  }

  const selectedRace = getSelectedRace()
  const raceId = selectedRace?.id

  // Load existing timestamps from local storage
  timestamps = loadLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, raceId)) || []

  // Load race start time if exists
  const raceData = loadLocal(getRaceKey(STORAGE_KEYS.RACE, raceId))
  if (raceData?.startTime) {
    raceStartTime = raceData.startTime
    raceFinished = raceData.status === 'finished'
  } else {
    raceStartTime = null
    raceFinished = false
  }

  return {
    deviceId,
    timestamps,
    raceStartTime,
    raceFinished
  }
}

/**
 * Start the race - sets gun time
 * @returns {number} The race start timestamp
 */
export function startRace() {
  raceStartTime = performance.timeOrigin + performance.now()
  raceFinished = false

  const selectedRace = getSelectedRace()
  const raceId = selectedRace?.id

  const raceData = {
    id: raceId || generateUUID(), // Ensure it matches race ID if possible
    startTime: raceStartTime,
    startedAt: new Date().toISOString(),
    deviceId,
    status: 'live'
  }

  saveLocal(getRaceKey(STORAGE_KEYS.RACE, raceId), raceData)
  queueForSync('race_start', raceData)

  return raceStartTime
}

/**
 * Stop the race - marks as finished
 */
export function stopRace() {
  if (!raceStartTime) return

  raceFinished = true

  const selectedRace = getSelectedRace()
  const raceId = selectedRace?.id

  const raceData = loadLocal(getRaceKey(STORAGE_KEYS.RACE, raceId)) || {}
  raceData.status = 'finished'
  raceData.finishedAt = new Date().toISOString()

  saveLocal(getRaceKey(STORAGE_KEYS.RACE, raceId), raceData)
  queueForSync('race_finish', raceData)

  return raceData
}

/**
 * Record a finish timestamp
 * @returns {Object} The recorded timestamp object
 */
export function recordFinish() {
  if (!raceStartTime) {
    console.warn('Race not started - recording absolute time')
  }

  const now = performance.timeOrigin + performance.now()
  const sequence = timestamps.length + 1
  const selectedRace = getSelectedRace()

  const timestamp = {
    id: generateUUID(),
    raceId: selectedRace?.id || null,
    rawTime: now,
    netTime: raceStartTime ? now - raceStartTime : null,
    sequence,
    deviceId,
    recordedAt: new Date().toISOString()
  }

  timestamps.push(timestamp)
  saveLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, selectedRace?.id), timestamps)
  queueForSync('timestamp', timestamp)

  // Trigger haptic feedback if available
  if (navigator.vibrate) {
    navigator.vibrate(50)
  }

  return timestamp
}

/**
 * Remove the last timestamp (undo)
 * @returns {Object|null} The removed timestamp or null if none
 */
export function undoLastFinish() {
  if (timestamps.length === 0) return null

  const removed = timestamps.pop()
  saveLocal(STORAGE_KEYS.TIMESTAMPS, timestamps)
  queueForSync('timestamp_delete', { id: removed.id })

  return removed
}

/**
 * Remove a specific timestamp by ID
 * Re-sequences remaining timestamps
 * @param {string} id - The timestamp ID to remove
 * @returns {Object|null} The removed timestamp or null if not found
 */
export function removeTimestamp(id) {
  const index = timestamps.findIndex(t => t.id === id)
  if (index === -1) return null

  const removed = timestamps.splice(index, 1)[0]

  // Re-sequence all timestamps after the removed one
  for (let i = index; i < timestamps.length; i++) {
    timestamps[i].sequence = i + 1
  }

  saveLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, getSelectedRace()?.id), timestamps)
  queueForSync('timestamp_delete', { id: removed.id })
  queueForSync('timestamps_resequence', { timestamps: timestamps.map(t => ({ id: t.id, sequence: t.sequence })) })

  return removed
}

/**
 * Insert a timestamp at a specific position
 * Use case: Operator missed a finish, needs to insert between existing times
 * @param {number} afterSequence - Insert after this sequence number (0 = insert at beginning)
 * @param {number} [netTimeMs] - Optional net time in ms (if known from video review)
 * @returns {Object} The inserted timestamp
 */
export function insertTimestamp(afterSequence, netTimeMs = null) {
  // Determine the insertion index
  const insertIndex = afterSequence

  // Calculate an estimated time if not provided
  // If between two timestamps, average them; otherwise use current time
  let estimatedNetTime = netTimeMs
  if (estimatedNetTime === null) {
    const before = timestamps[insertIndex - 1]
    const after = timestamps[insertIndex]

    if (before && after) {
      // Midpoint between two existing timestamps
      estimatedNetTime = (before.netTime + after.netTime) / 2
    } else if (before) {
      // After the last timestamp, add a small delta
      estimatedNetTime = before.netTime + 100 // 0.1 second after
    } else if (after) {
      // Before the first timestamp, subtract a small delta
      estimatedNetTime = after.netTime - 100 // 0.1 second before
    } else {
      // No timestamps exist, use current elapsed time
      estimatedNetTime = getElapsedTime()
    }
  }

  const selectedRace = getSelectedRace()
  const timestamp = {
    id: generateUUID(),
    raceId: selectedRace?.id || null,
    rawTime: raceStartTime ? raceStartTime + estimatedNetTime : null,
    netTime: estimatedNetTime,
    sequence: insertIndex + 1,
    deviceId,
    recordedAt: new Date().toISOString(),
    inserted: true // Flag to indicate this was manually inserted
  }

  // Insert at position
  timestamps.splice(insertIndex, 0, timestamp)

  // Re-sequence all timestamps after the inserted one
  for (let i = insertIndex + 1; i < timestamps.length; i++) {
    timestamps[i].sequence = i + 1
  }

  saveLocal(STORAGE_KEYS.TIMESTAMPS, timestamps)
  queueForSync('timestamp_insert', timestamp)
  queueForSync('timestamps_resequence', { timestamps: timestamps.map(t => ({ id: t.id, sequence: t.sequence })) })

  // Haptic feedback
  if (navigator.vibrate) {
    navigator.vibrate([25, 50, 25]) // Double tap pattern for insert
  }

  return timestamp
}

/**
 * Update a timestamp's net time (for corrections from video review)
 * @param {string} id - The timestamp ID
 * @param {number} newNetTimeMs - The corrected net time in ms
 * @returns {Object|null} The updated timestamp or null if not found
 */
export function updateTimestampTime(id, newNetTimeMs) {
  const timestamp = timestamps.find(t => t.id === id)
  if (!timestamp) return null

  timestamp.netTime = newNetTimeMs
  timestamp.rawTime = raceStartTime ? raceStartTime + newNetTimeMs : null
  timestamp.corrected = true // Flag to indicate this was manually corrected

  saveLocal(STORAGE_KEYS.TIMESTAMPS, timestamps)
  queueForSync('timestamp_update', timestamp)

  return timestamp
}

/**
 * Get all recorded timestamps
 */
export function getTimestamps() {
  return [...timestamps]
}

/**
 * Format milliseconds to display time (MM:SS.ss)
 */
export function formatTime(ms) {
  if (ms == null) return '--:--.--'

  const totalSeconds = ms / 1000
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes.toString().padStart(2, '0')}:${seconds.toFixed(2).padStart(5, '0')}`
}

/**
 * Parse time string to milliseconds
 * Supports formats: MM:SS.ss, MM:SS, M:SS.ss, SS.ss, etc.
 * @param {string} timeStr - Time string like "18:23.45" or "5:30"
 * @returns {number|null} Milliseconds or null if invalid
 */
export function parseTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null

  // Clean up input
  timeStr = timeStr.trim()

  // Try MM:SS.ss or M:SS.ss format
  const colonMatch = timeStr.match(/^(\d+):(\d{1,2}(?:\.\d+)?)$/)
  if (colonMatch) {
    const minutes = parseInt(colonMatch[1], 10)
    const seconds = parseFloat(colonMatch[2])
    if (isNaN(minutes) || isNaN(seconds) || seconds >= 60) return null
    return (minutes * 60 + seconds) * 1000
  }

  // Try SS.ss format (seconds only)
  const secondsMatch = timeStr.match(/^(\d+(?:\.\d+)?)$/)
  if (secondsMatch) {
    const seconds = parseFloat(secondsMatch[1])
    if (isNaN(seconds)) return null
    return seconds * 1000
  }

  return null
}

/**
 * Add a timestamp with a manually specified time
 * @param {number} netTimeMs - Net time in milliseconds
 * @param {number} [afterSequence] - Insert after this sequence (null = append to end)
 * @returns {Object} The created timestamp
 */
export function addManualTimestamp(netTimeMs, afterSequence = null) {
  if (afterSequence !== null) {
    return insertTimestamp(afterSequence, netTimeMs)
  }

  // Append to end
  const sequence = timestamps.length + 1
  const selectedRace = getSelectedRace()

  const timestamp = {
    id: generateUUID(),
    raceId: selectedRace?.id || null,
    rawTime: raceStartTime ? raceStartTime + netTimeMs : null,
    netTime: netTimeMs,
    sequence,
    deviceId,
    recordedAt: new Date().toISOString(),
    manual: true // Flag to indicate this was manually entered
  }

  timestamps.push(timestamp)
  saveLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, selectedRace?.id), timestamps)
  queueForSync('timestamp_insert', timestamp)

  // Sort by netTime to maintain order
  timestamps.sort((a, b) => a.netTime - b.netTime)

  // Re-sequence
  timestamps.forEach((t, i) => {
    t.sequence = i + 1
  })

  saveLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, selectedRace?.id), timestamps)
  queueForSync('timestamps_resequence', { timestamps: timestamps.map(t => ({ id: t.id, sequence: t.sequence })) })

  return timestamp
}

/**
 * Get elapsed time since race start
 */
export function getElapsedTime() {
  if (!raceStartTime) return null
  return performance.timeOrigin + performance.now() - raceStartTime
}

/**
 * Check if race is active
 */
export function isRaceActive() {
  return raceStartTime !== null && !raceFinished
}

/**
 * Check if race is finished
 */
export function isRaceFinished() {
  return raceFinished
}

/**
 * Merge remote timestamps with local timestamps
 * Used when receiving updates from Firebase on other devices
 * @param {Array} remoteTimestamps - Timestamps from Firebase
 */
export function mergeRemoteTimestamps(remoteTimestamps) {
  if (!remoteTimestamps || remoteTimestamps.length === 0) {
    return timestamps
  }

  // Create a map of local timestamps by ID for quick lookup
  const localMap = new Map(timestamps.map(t => [t.id, t]))

  // Merge: use remote timestamps, preserving local-only ones
  const mergedMap = new Map()

  // Add all remote timestamps
  remoteTimestamps.forEach(remote => {
    mergedMap.set(remote.id, remote)
  })

  // Add local timestamps that aren't in remote (e.g., queued for sync)
  timestamps.forEach(local => {
    if (!mergedMap.has(local.id)) {
      mergedMap.set(local.id, local)
    }
  })

  // Convert to array and sort by sequence
  const merged = Array.from(mergedMap.values())
  merged.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))

  // Update local state
  timestamps = merged

  // Save to localStorage
  const selectedRace = getSelectedRace()
  saveLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, selectedRace?.id), timestamps)

  return timestamps
}

/**
 * Set timestamps directly (used for initial load from Firebase)
 * @param {Array} newTimestamps - Timestamps to set
 */
export function setTimestamps(newTimestamps) {
  timestamps = newTimestamps || []
  const selectedRace = getSelectedRace()
  saveLocal(getRaceKey(STORAGE_KEYS.TIMESTAMPS, selectedRace?.id), timestamps)
  return timestamps
}

/**
 * Set master timer lock data
 */
export function setMasterTimerLock(lock) {
  masterTimerLock = lock
}

/**
 * Check if current device is the master timer
 */
export function isMasterTimer() {
  if (!masterTimerLock) return true // If no lock exists, any device can timing (becomes master)
  return masterTimerLock.masterDeviceId === deviceId
}

/**
 * Get current master device ID
 */
export function getMasterDeviceId() {
  return masterTimerLock?.masterDeviceId || null
}

export { deviceId, raceStartTime }
